import { Brand } from '../model/brand.model';
import { resolveActiveBrand } from '../service/brand-resolver';

/**
 * Sets the browser-tab title and favicon and the theme custom properties from
 * the host-resolved brand before Angular bootstraps.
 *
 * index.html carries the default title and favicon statically, so the default
 * host never flashes; this only diverges for a non-default host. It runs
 * pre-bootstrap and therefore cannot use Angular DI — it shares resolveActiveBrand
 * with BrandService so the early chrome and the in-app service never disagree
 * about which brand is active.
 */
export function applyBrandChrome(doc: Document = document): void {
  const brand = resolveActiveBrand();
  doc.title = brand.name;
  setFavicon(doc, brand.favicon);
  applyTheme(doc, brand.theme);
}

/**
 * Writes the brand theme onto the document root as CSS custom properties, so
 * branded controls (the login CTA button) and the auth backdrop follow the
 * resolved brand. These are the same properties button.component.scss and the
 * auth layout fall back on, so the default brand needs no inline values.
 */
function applyTheme(doc: Document, theme: Brand['theme']): void {
  const root = doc.documentElement;
  root.style.setProperty('--button-brand-bg', theme.buttonBg);
  root.style.setProperty('--button-brand-bg-hover', theme.buttonBgHover);
  root.style.setProperty('--button-brand-shadow', theme.buttonShadow);
  root.style.setProperty('--auth-background-image', theme.loginBackgroundImage);
  root.style.setProperty('--auth-background-color', theme.loginBackgroundColor);
}

function setFavicon(doc: Document, href: string): void {
  let link = doc.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (!link) {
    link = doc.createElement('link');
    link.rel = 'icon';
    doc.head.appendChild(link);
  }
  link.href = href;
}
